import React, { forwardRef, useImperativeHandle } from 'react';
import { Button, IconButton, LinearProgress, Stack, Typography } from '@mui/material';
import axios from 'axios';
import AddIcon from '@mui/icons-material/Add';
import { FieldNavigationCard, FieldNavigationList, FieldNavigationOption } from './FieldNavigation';
import AddSeasonDialog from './AddSeasonDialog';
import CloseIcon from '@mui/icons-material/Close';
import { useOnboarding } from '../onboarding/OnboardingProvider';
import useToken from '../../services/token';
const ListField = forwardRef((props, ref) => {
    var _a, _b;
    const { hasTask, startTask } = useOnboarding();
    const { parseJwt } = useToken();
    const role = (_b = (_a = parseJwt(props.token)) === null || _a === void 0 ? void 0 : _a.role) !== null && _b !== void 0 ? _b : null;
    const canManageFields = role === 'admin' || role === 'Главный агроном';
    const [list, setList] = React.useState([]);
    const [current, setCurrent] = React.useState(null);
    const [load, setLoad] = React.useState(Boolean(props.token));
    const [loadError, setLoadError] = React.useState(false);
    const requestVersion = React.useRef(0);
    const [expandedId, setExpandedId] = React.useState(null);
    const [seasonsByField, setSeasonsByField] = React.useState({});
    const [selectedSeason, setSelectedSeason] = React.useState(null);
    const [addSeasonFieldId, setAddSeasonFieldId] = React.useState(null);
    useImperativeHandle(ref, () => ({
        update: update
    }));
    React.useEffect(() => {
        update();
    }, []);
    const update = () => {
        if (props.token) {
            const version = ++requestVersion.current;
            setLoad(true);
            setLoadError(false);
            axios.get('/field/list', {
                headers: {
                    "Content-Type": "application/json",
                    "Authorization": `Bearer ${props.token}`
                }
            })
                .then((response) => {
                if (version !== requestVersion.current)
                    return;
                if (Array.isArray(response.data)) {
                    let items = response.data.map((i) => {
                        i.date_created = new Date(i.date_created);
                        return i;
                    });
                    setList(items);
                }
                else
                    setLoadError(true);
            }).catch(() => {
                if (version === requestVersion.current)
                    setLoadError(true);
            }).finally(() => {
                if (version === requestVersion.current)
                    setLoad(false);
            });
        }
    };
    const loadSeasons = (fieldId) => {
        axios.get(`/field/season/list/${fieldId}`, {
            headers: {
                "Content-Type": "application/json",
                "Authorization": `Bearer ${props.token}`
            }
        }).then((response) => {
            if (response.data) {
                const seasons = response.data;
                setSeasonsByField((prev) => (Object.assign(Object.assign({}, prev), { [fieldId]: seasons })));
            }
        });
    };
    const toggleField = (item) => {
        const willExpand = expandedId !== item.id;
        setExpandedId(willExpand ? item.id : null);
        if (willExpand && !seasonsByField[item.id]) {
            loadSeasons(item.id);
        }
    };
    const selectSeason = (item, season) => {
        setCurrent(item);
        setSelectedSeason(season);
    };
    const closeDashboard = (e) => {
        e.stopPropagation(); // чтобы клик по крестику не сработал как клик по шапке (сворачивание/разворачивание)
        setCurrent(null);
        setSelectedSeason(null);
    };
    // plot_id того же сезона — новые /spectral/* роуты адресуются по плоту,
    // не по field+year. Не отдельный стейт: всегда пересчитывается из уже
    // загруженного списка сезонов, не может рассинхронизироваться.
    const selectedPlotId = React.useMemo(() => {
        var _a, _b, _c;
        if (current === null || selectedSeason === null)
            return null;
        const seasons = (_a = seasonsByField[current.id]) !== null && _a !== void 0 ? _a : [];
        return (_c = (_b = seasons.find((s) => s.season === selectedSeason)) === null || _b === void 0 ? void 0 : _b.id) !== null && _c !== void 0 ? _c : null;
    }, [current, selectedSeason, seasonsByField]);
    React.useEffect(() => {
        props.select(current, selectedSeason, selectedPlotId);
    }, [current, selectedSeason, selectedPlotId]);
    return (React.createElement(React.Fragment, null,
        load == true && (React.createElement(LinearProgress, { "data-onboarding-state": "loading", color: "success" })),
        !load && loadError && (React.createElement(Stack, { "data-onboarding-state": "error", alignItems: "center", sx: { pt: 2 } },
            React.createElement(Typography, { variant: "caption", color: "error" }, "\u041D\u0435 \u0443\u0434\u0430\u043B\u043E\u0441\u044C \u0437\u0430\u0433\u0440\u0443\u0437\u0438\u0442\u044C \u043F\u043E\u043B\u044F."),
            React.createElement(Button, { size: "small", onClick: update }, "\u041F\u043E\u0432\u0442\u043E\u0440\u0438\u0442\u044C"))),
        !load && !loadError && list.length === 0 && (React.createElement(Stack, { "data-onboarding-state": "empty", alignItems: "center", sx: { pt: 2 } },
            React.createElement(Typography, { variant: 'body2', color: "text.secondary", align: "center" }, role === 'Работник' ? 'Ожидается назначение полей' : role === 'user' ? 'Ожидается назначение роли' : 'Список полей пуст'),
            role === 'Работник' && React.createElement(Typography, { variant: "caption", color: "text.secondary", align: "center", sx: { px: 1 } }, "\u0410\u0434\u043C\u0438\u043D\u0438\u0441\u0442\u0440\u0430\u0442\u043E\u0440 \u0438\u043B\u0438 \u0433\u043B\u0430\u0432\u043D\u044B\u0439 \u0430\u0433\u0440\u043E\u043D\u043E\u043C \u043E\u0442\u043A\u0440\u043E\u0435\u0442 \u0432\u0430\u043C \u0434\u043E\u0441\u0442\u0443\u043F \u043A \u043D\u0443\u0436\u043D\u044B\u043C \u043F\u043E\u043B\u044F\u043C."),
            hasTask('open-field') && React.createElement(Button, { size: "small", onClick: () => startTask('open-field') }, "\u0427\u0442\u043E \u0434\u0435\u043B\u0430\u0442\u044C?"))),
        React.createElement(FieldNavigationList, null, list.map((item) => {
            var _a, _b;
            return (React.createElement(FieldNavigationCard, { key: item.id, name: item.name, expanded: expandedId === item.id, selected: (current === null || current === void 0 ? void 0 : current.id) === item.id, onToggle: () => toggleField(item), action: (current === null || current === void 0 ? void 0 : current.id) === item.id && selectedSeason !== null ? (React.createElement(IconButton, { size: "small", "aria-label": "\u0417\u0430\u043A\u0440\u044B\u0442\u044C \u0434\u0430\u043D\u043D\u044B\u0435 \u043F\u043E\u043B\u044F", onClick: closeDashboard, sx: { p: 0.25 } },
                    React.createElement(CloseIcon, { fontSize: "small" }))) : undefined },
                ((_a = seasonsByField[item.id]) !== null && _a !== void 0 ? _a : []).length === 0 && (React.createElement(Typography, { variant: "caption", color: "text.secondary", sx: { pl: 1 } }, "\u041D\u0435\u0442 \u0441\u0435\u0437\u043E\u043D\u043E\u0432")),
                ((_b = seasonsByField[item.id]) !== null && _b !== void 0 ? _b : []).map((s) => (React.createElement(FieldNavigationOption, { key: s.season, selected: (current === null || current === void 0 ? void 0 : current.id) === item.id && selectedSeason === s.season, onClick: () => selectSeason(item, s.season) },
                    "\u0421\u0435\u0437\u043E\u043D ",
                    s.season))),
                canManageFields && React.createElement(Button, { size: "small", startIcon: React.createElement(AddIcon, { fontSize: "inherit" }), onClick: () => setAddSeasonFieldId(item.id), sx: { justifyContent: 'flex-start', px: 2, py: 0.75, color: 'text.secondary', textTransform: 'none' } }, "\u0414\u043E\u0431\u0430\u0432\u0438\u0442\u044C \u0441\u0435\u0437\u043E\u043D")));
        })),
        canManageFields && React.createElement(AddSeasonDialog, { token: props.token, fieldId: addSeasonFieldId !== null && addSeasonFieldId !== void 0 ? addSeasonFieldId : 0, open: addSeasonFieldId !== null, setState: (x) => !x && setAddSeasonFieldId(null), onCreated: () => {
                if (addSeasonFieldId !== null) {
                    loadSeasons(addSeasonFieldId);
                }
            } })));
});
export default ListField;
